import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronRight,
  Car,
  Plane,
  Sparkles,
  Shield,
  Clock,
  Phone,
  Star,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export const Pricing = () => {
  const { t } = useLanguage();

  const pricingPlans = [
    {
      name: t.pricing.shuttle.name,
      icon: Plane,
      description: t.pricing.shuttle.desc,
      price: t.pricing.shuttle.price,
      unit: t.pricing.shuttle.unit,
      weekPrice: t.pricing.shuttle.weekPrice,
      features: t.pricing.shuttle.features,
      popular: false,
      cta: t.pricing.shuttle.cta,
    },
    {
      name: t.pricing.valet.name,
      icon: Car,
      description: t.pricing.valet.desc,
      price: t.pricing.valet.price,
      unit: t.pricing.valet.unit,
      weekPrice: t.pricing.valet.weekPrice,
      features: t.pricing.valet.features,
      popular: true,
      cta: t.pricing.valet.cta,
      popularLabel: t.pricing.valet.popular,
    },
    {
      name: t.pricing.carcare.name,
      icon: Sparkles,
      description: t.pricing.carcare.desc,
      price: t.pricing.carcare.price,
      unit: t.pricing.carcare.unit,
      weekPrice: t.pricing.carcare.weekPrice,
      features: t.pricing.carcare.features,
      popular: false,
      cta: t.pricing.carcare.cta,
    },
  ];

  const includedItems = [
    { icon: Shield, ...t.pricing.included[0] },
    { icon: Clock, ...t.pricing.included[1] },
    { icon: Star, ...t.pricing.included[2] },
    { icon: Phone, ...t.pricing.included[3] },
  ];

  return (
    <div className="overflow-hidden pt-20" data-testid="pricing-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.pricing.subtitle}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {t.pricing.title1}{' '}
              <span className="text-[#D4AF37]">{t.pricing.title2}</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg leading-relaxed">
              {t.pricing.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative overflow-hidden rounded-3xl ${
                  plan.popular
                    ? 'bg-gradient-to-b from-[#D4AF37]/10 to-transparent border-2 border-[#D4AF37]'
                    : 'bg-[#0A0A0A] border border-white/10'
                } p-8`}
                data-testid={`pricing-card-${index}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-[#D4AF37] text-black text-xs font-bold px-4 py-1 rounded-bl-lg">
                    {plan.popularLabel}
                  </div>
                )}

                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                  <plan.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>

                <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                <p className="text-white/60 text-sm mb-6">{plan.description}</p>

                <div className="mb-2">
                  <span className="text-4xl md:text-5xl font-bold text-white">{plan.price}</span>
                  <span className="text-white/60 text-lg ml-1">{plan.unit}</span>
                </div>
                <p className="text-[#D4AF37] text-sm font-medium mb-8">{plan.weekPrice}</p>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-white/80 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://mcdgroup.de/buchungssystem/index.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full inline-flex items-center justify-center gap-2 py-4 rounded-full font-semibold transition-all duration-300 ${
                    plan.popular
                      ? 'bg-[#D4AF37] text-black hover:bg-[#E5C34A] gold-glow'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid={`pricing-cta-${index}`}
                >
                  {plan.cta}
                  <ChevronRight className="w-5 h-5" />
                </a>
              </motion.div>
            ))}
          </div>

          <motion.p
            {...fadeUp}
            viewport={{ once: true }}
            className="text-center text-white/50 text-sm mt-8"
          >
            {t.pricing.priceNote}
          </motion.p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div {...fadeUp} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
              {t.pricing.includedSubtitle}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              {t.pricing.includedTitle}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {includedItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Price FAQs */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div {...fadeUp} viewport={{ once: true }}>
              <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-widest mb-4 block">
                {t.pricing.faqSubtitle}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                {t.pricing.faqTitle}
              </h2>
              <p className="text-white/60 text-base leading-relaxed mb-8">
                {t.pricing.faqDesc}
              </p>
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-[#D4AF37] font-medium hover:underline"
              >
                {t.pricing.viewAllFaq}
                <ChevronRight className="w-5 h-5" />
              </Link>
            </motion.div>

            <motion.div {...fadeUp} viewport={{ once: true }} className="space-y-4">
              {t.pricing.faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <h3 className="text-white font-medium mb-2">{faq.q}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div
            {...fadeUp}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              {t.pricing.ctaTitle}
            </h2>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-8">
              {t.pricing.ctaDesc}
            </p>
            <a
              href="https://mcdgroup.de/buchungssystem/index.php"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-10 py-5 rounded-full font-semibold text-lg hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
              data-testid="pricing-main-cta"
            >
              {t.pricing.ctaBtn}
              <ChevronRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
